import { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import apiClient from "../../services/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

// US States list with 2-letter codes
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "Washington DC" },
];

/**
 * Reusable Address Fields Component
 * Provides consistent address input fields across Employee, Contractor, Supplier, and Job forms
 * 
 * @param {Object} props
 * @param {Object} props.values - Object containing street, apt, city, state, zip_code values
 * @param {Function} props.onChange - Function to update form state: (field, value) => void
 * @param {string} props.prefix - Optional prefix for data-testid attributes (e.g., "employee", "gc", "supplier", "job")
 * @param {string} props.title - Optional section title (default: "Address")
 * @param {boolean} props.showBorder - Whether to show top border (default: true)
 * @param {boolean} props.compact - Use compact 3-column layout for city/state/zip (default: false)
 */
const AddressFields = ({ 
  values = {}, 
  onChange, 
  prefix = "", 
  title = "Address",
  showBorder = true,
  compact = false,
  autocomplete = true,
  onChangeMany,
}) => {
  const testIdPrefix = prefix ? `${prefix}-` : "";
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const typing = useRef(false); // true only right after the user typed in the Street field
  const ctx = useRef({ city: values.city, state: values.state });
  ctx.current = { city: values.city, state: values.state };

  // Search only while the user is typing in Street — never re-open because City/State/ZIP changed.
  useEffect(() => {
    if (!autocomplete) return;
    if (!typing.current) return;
    typing.current = false;
    const q = (values.street || "").trim();
    clearTimeout(timer.current);
    if (q.length < 3) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await apiClient.get(`/geocode/suggest`, { params: { q: [q, ctx.current.city, ctx.current.state].filter(Boolean).join(" ") } });
        const list = res.data.suggestions || [];
        setSuggestions(list);
        setOpen(list.length > 0 && document.activeElement?.id === `${testIdPrefix}street`);
      } catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [values.street, autocomplete, testIdPrefix]);

  const dismiss = () => { clearTimeout(timer.current); setOpen(false); setSuggestions([]); };

  const pick = (s) => {
    const patch = { street: s.street, city: s.city, state: s.state, zip_code: s.zip_code };
    if (onChangeMany) onChangeMany(patch);
    else Object.entries(patch).forEach(([k, v]) => onChange(k, v));
    dismiss();
  };
  
  const handleChange = (field) => (e) => {
    if (field === "street") typing.current = true;
    onChange(field, e.target.value);
  };

  // Format ZIP code (5 digits only for US)
  const handleZipChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 5);
    onChange("zip_code", value);
  };

  return (
    <div className={`space-y-3 ${showBorder ? "pt-2 border-t" : ""}`}>
      <Label className="font-medium">{title}</Label>
      
      <div className="form-group relative">
        <Label htmlFor={`${testIdPrefix}street`} className="text-sm text-muted-foreground">
          Street
        </Label>
        <Input
          id={`${testIdPrefix}street`}
          value={values.street || ""}
          onChange={handleChange("street")}
          onBlur={() => setTimeout(dismiss, 150)}
          onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Tab") dismiss(); }}
          placeholder="123 Main St"
          autoComplete="off"
          data-testid={`${testIdPrefix}street-input`}
        />
        {open && suggestions.length > 0 && (
          <ul className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-md border bg-popover shadow-md text-sm" data-testid={`${testIdPrefix}address-suggestions`}>
            {suggestions.map((s, i) => (
              <li key={`${s.label}-${i}`}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)}
                  className="w-full text-left px-3 py-2 hover:bg-accent flex items-start gap-2" data-testid={`${testIdPrefix}address-suggestion-${i}`}>
                  <MapPin className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
                  <span>{s.label}</span>
                </button>
              </li>
            ))}
            <li className="px-3 py-1 text-[10px] text-muted-foreground border-t">© OpenStreetMap contributors</li>
          </ul>
        )}
      </div>

      <div className="form-group">
        <Label htmlFor={`${testIdPrefix}apt`} className="text-sm text-muted-foreground">
          Apt/Suite/Unit
        </Label>
        <Input
          id={`${testIdPrefix}apt`}
          value={values.apt || ""}
          onChange={handleChange("apt")}
          placeholder="Apt 4B"
          autoComplete="v2r-no-autofill"
          data-testid={`${testIdPrefix}apt-input`}
        />
      </div>

      <div className={`grid ${compact ? "grid-cols-3" : "grid-cols-2 md:grid-cols-3"} gap-3`}>
        <div className="form-group">
          <Label htmlFor={`${testIdPrefix}city`} className="text-sm text-muted-foreground">
            City
          </Label>
          <Input
            id={`${testIdPrefix}city`}
            value={values.city || ""}
            onChange={handleChange("city")}
            placeholder="City"
            autoComplete="v2r-no-autofill"
            data-testid={`${testIdPrefix}city-input`}
          />
        </div>
        <div className="form-group">
          <Label htmlFor={`${testIdPrefix}state`} className="text-sm text-muted-foreground">
            State
          </Label>
          <Select 
            value={values.state || ""} 
            onValueChange={(value) => onChange("state", value)}
          >
            <SelectTrigger data-testid={`${testIdPrefix}state-select`}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.code} - {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="form-group">
          <Label htmlFor={`${testIdPrefix}zip_code`} className="text-sm text-muted-foreground">
            ZIP Code
          </Label>
          <Input
            id={`${testIdPrefix}zip_code`}
            value={values.zip_code || ""}
            onChange={handleZipChange}
            placeholder="12345"
            maxLength={5}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="v2r-no-autofill"
            data-testid={`${testIdPrefix}zip-input`}
          />
        </div>
      </div>
    </div>
  );
};

export default AddressFields;
